/** Where the backend's OAuth `/oauth/authorize` sends the operator to consent. */
export const ASSISTANT_AUTHORIZE_PATH = '/assistant/authorize';

/** sessionStorage key holding the authorize URL across a login. */
export const POST_LOGIN_RETURN_KEY = 'assistant.postLoginReturn';

/**
 * Reads and clears the remembered authorize URL. Returns it only if it is an
 * in-app path to the authorize page — anything else is discarded, so this can
 * never become an open redirect.
 */
export const consumePostLoginReturn = (): string | null => {
  let url: string | null = null;
  try {
    url = sessionStorage.getItem(POST_LOGIN_RETURN_KEY);
    sessionStorage.removeItem(POST_LOGIN_RETURN_KEY);
  } catch {
    return null;
  }
  if (!url || !url.startsWith(ASSISTANT_AUTHORIZE_PATH)) return null;
  return url;
};

/** Read-only access the connector asks for. Must match the backend's scope. */
export const ASSISTANT_SCOPE = 'backoffice:read';

/**
 * How an operator hooks a provider up to the backoffice:
 * - `connector`: pastes the MCP URL into the provider's connector settings.
 * - `command`: runs a CLI command that already carries the MCP URL.
 */
export type AssistantProviderSetup = 'connector' | 'command';

export interface AssistantProvider {
  key: string;
  /** Product name. A proper noun, so it is not translated. */
  name: string;
  /** Font Awesome 4 icon shown in the card avatar. */
  icon: string;
  /** i18n key under `assistant.providers.<key>` for the surface (web, terminal…). */
  surfaceKey: string;
  setup: AssistantProviderSetup;
  /** Shows the BETA ribbon on the card. */
  beta?: boolean;
  /** i18n keys of the numbered steps shown on the card. */
  stepKeys: string[];
  /** CLI command template; `{{url}}` is replaced with the MCP URL. Only for `command`. */
  command?: string;
}

/**
 * Providers shown on the connect page. Only list the ones whose OAuth callback
 * the backend accepts (today claude.ai, chatgpt.com and localhost) — a card for anything
 * else would fail at the authorize step.
 */
export const ASSISTANT_PROVIDERS: AssistantProvider[] = [
  {
    key: 'claudeApp',
    name: 'Claude',
    icon: 'fa-desktop',
    surfaceKey: 'assistant.providers.claudeApp.surface',
    setup: 'connector',
    beta: true,
    stepKeys: [
      'assistant.providers.claudeApp.step1',
      'assistant.providers.claudeApp.step2',
      'assistant.providers.claudeApp.step3',
    ],
  },
  {
    key: 'claudeCode',
    name: 'Claude Code',
    icon: 'fa-terminal',
    surfaceKey: 'assistant.providers.claudeCode.surface',
    setup: 'command',
    beta: true,
    stepKeys: [
      'assistant.providers.claudeCode.step1',
      'assistant.providers.claudeCode.step2',
    ],
    command: 'claude mcp add --transport http backoffice {{url}}',
  },
  {
    key: 'chatgpt',
    name: 'ChatGPT',
    icon: 'fa-comments',
    surfaceKey: 'assistant.providers.chatgpt.surface',
    setup: 'connector',
    beta: true,
    stepKeys: [
      'assistant.providers.chatgpt.step1',
      'assistant.providers.chatgpt.step2',
      'assistant.providers.chatgpt.step3',
    ],
  },
];
